import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Layout } from '@/components/layout/Layout';
import { MetricCard } from '@/components/common/MetricCard';
import { Smartphone, Users, AlertTriangle, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import {
  useDashboardMetrics,
  useDeviceActivity,
  useErrorsByType,
  useRecentDevices,
} from '@/hooks/useDashboard';
import { useRealtimeLogs } from '@/hooks/useRealtimeLogs';
import { useRealtimeDevices } from '@/hooks/useRealtimeDevices';
import { formatRelativeTime, formatChartDate } from '@/utils/formatters';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

export function Dashboard() {
  const navigate = useNavigate();
  const [activityPeriod, setActivityPeriod] = useState<'24h' | 'yesterday' | '7d' | '30d' | '3m'>(
    '7d'
  );

  const {
    data: metricsData,
    isLoading: metricsLoading,
    error: metricsError,
    refetch: refetchMetrics,
  } = useDashboardMetrics();
  const { data: activityData, isLoading: activityLoading } = useDeviceActivity(activityPeriod);
  const { data: errorsData, isLoading: errorsLoading } = useErrorsByType();
  const { data: recentDevicesData, isLoading: devicesLoading } = useRecentDevices(5);

  const { criticalErrors, isConnected } = useRealtimeLogs(50);

  useRealtimeDevices();

  useEffect(() => {
    if (criticalErrors.length > 0) {
      refetchMetrics();
    }
  }, [criticalErrors.length, refetchMetrics]);

  if (metricsError) {
    return (
      <Layout breadcrumbs={[{ label: 'Página Inicial', path: '/' }, { label: 'Dashboard' }]}>
        <div className="min-h-full bg-bg-main p-3 sm:p-4 lg:p-6">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <AlertTriangle className="mx-auto h-12 w-12 text-error mb-4" />
              <h2 className="mb-2 text-xl font-semibold text-text-title">
                Erro ao carregar dashboard
              </h2>
              <p className="text-sm text-text-secondary">
                Não foi possível carregar os dados. Tente novamente.
              </p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const metrics = {
    totalDevices: metricsData?.totalDevices ?? 0,
    activeDevices: metricsData?.activeDevices ?? 0,
    totalErrors: metricsData?.totalErrors ?? 0,
    criticalErrors: metricsData?.criticalErrors ?? 0,
    totalUsers: metricsData?.totalUsers ?? 0,
    activeUsers: metricsData?.activeUsers ?? 0,
    unresolvedErrors: metricsData?.unresolvedErrors ?? 0,
    avgResponseTime: metricsData?.avgResponseTime ?? 0,
  };

  const recentDevices = recentDevicesData?.devices || [];

  return (
    <Layout breadcrumbs={[{ label: 'Página Inicial', path: '/' }, { label: 'Dashboard' }]}>
      <div className="min-h-full bg-bg-main p-3 sm:p-4 lg:p-6">
        <div className="flex flex-col gap-4 lg:gap-6">
          {/* Título */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="text-2xl font-semibold tracking-tight text-text-title sm:text-3xl">
              Dashboard de Monitoramento
            </h1>
            <div
              className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium ${
                isConnected ? 'bg-online/10 text-online' : 'bg-offline/10 text-offline'
              }`}
            >
              {isConnected ? <Wifi size={16} /> : <WifiOff size={16} />}
              <span>{isConnected ? 'Tempo Real Ativo' : 'Tempo Real Desconectado'}</span>
            </div>
          </div>

          {/* Cards de Métricas */}
          <div className="grid grid-cols-1 gap-3 xs:grid-cols-2 lg:grid-cols-4 lg:gap-6">
            <MetricCard
              title="Dispositivos Ativos"
              value={metricsLoading ? '...' : metrics.activeDevices.toLocaleString()}
              subtitle={`Total: ${metrics.totalDevices}`}
              icon={Smartphone}
            />

            <MetricCard
              title="Usuários Ativos"
              value={metricsLoading ? '...' : metrics.activeUsers.toLocaleString()}
              subtitle={`Total: ${metrics.totalUsers}`}
              icon={Users}
              action={{ label: 'Ver todos', onClick: () => navigate('/admin') }}
            />

            <MetricCard
              title="Erros Críticos"
              value={metricsLoading ? '...' : metrics.criticalErrors.toString()}
              subtitle={`${metrics.unresolvedErrors} não resolvidos`}
              icon={AlertTriangle}
              valueColor="text-error"
              action={{ label: 'Ver detalhes', onClick: () => navigate('/errors') }}
            />

            <MetricCard
              title="Tempo Médio de Resposta"
              value={metricsLoading ? '...' : `${metrics.avgResponseTime}ms`}
              subtitle="Performance da API"
              icon={RefreshCw}
            />
          </div>

          {/* Gráficos */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Gráfico de Linha - Atividade de Dispositivos */}
            <div className="lg:col-span-3 flex flex-col gap-4 rounded-lg p-6 bg-card border border-border shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-xl font-semibold text-text-title">
                  Atividade de Dispositivos
                </h3>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: '24h', label: 'Últimas 24h' },
                    { value: 'yesterday', label: 'Ontem' },
                    { value: '7d', label: 'Últimos 7 dias' },
                    { value: '30d', label: 'Últimos 30 dias' },
                    { value: '3m', label: 'Últimos 3 meses' },
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => setActivityPeriod(value as typeof activityPeriod)}
                      aria-pressed={activityPeriod === value}
                      className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                        activityPeriod === value
                          ? 'bg-primary text-white shadow-sm'
                          : 'bg-muted text-text-secondary hover:bg-gray-200'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {activityLoading ? (
                <div className="flex items-center justify-center h-[300px]">
                  <RefreshCw className="size-8 animate-spin text-primary" />
                </div>
              ) : activityData && activityData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={activityData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis
                      dataKey="date"
                      stroke="#57636C"
                      fontSize={12}
                      tickFormatter={(value) => formatChartDate(value, activityPeriod)}
                    />
                    <YAxis stroke="#57636C" fontSize={12} />
                    <Tooltip
                      labelFormatter={(value) => formatChartDate(value as string, activityPeriod)}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="peak"
                      name={
                        activityPeriod === '24h' || activityPeriod === 'yesterday'
                          ? 'Pico horário'
                          : activityPeriod === '3m'
                            ? 'Pico mensal'
                            : 'Pico diário'
                      }
                      stroke="#003E6B"
                      strokeWidth={2}
                      dot={{ fill: '#003E6B' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="average"
                      name={
                        activityPeriod === '24h' || activityPeriod === 'yesterday'
                          ? 'Média horária'
                          : activityPeriod === '3m'
                            ? 'Média mensal'
                            : 'Média diária'
                      }
                      stroke="#4CAF50"
                      strokeWidth={2}
                      dot={{ fill: '#4CAF50' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px]">
                  <p className="text-text-secondary">Sem dados disponíveis</p>
                </div>
              )}
            </div>

            {/* Gráfico de Barras - Erros por Tipo */}
            <div className="lg:col-span-2 flex flex-col gap-4 rounded-lg p-6 bg-card border border-border shadow-sm">
              <h3 className="text-xl font-semibold text-text-title">
                Erros por Tipo (Últimas 24h)
              </h3>
              {errorsLoading ? (
                <div className="flex items-center justify-center h-[300px]">
                  <RefreshCw className="size-8 animate-spin text-primary" />
                </div>
              ) : errorsData && errorsData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={errorsData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="type" stroke="#57636C" fontSize={12} />
                    <YAxis stroke="#57636C" fontSize={12} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#003E6B" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px]">
                  <p className="text-text-secondary">Sem dados disponíveis</p>
                </div>
              )}
            </div>
          </div>

          {/* Tabela de Dispositivos */}
          <div className="rounded-lg bg-card border border-border shadow-sm overflow-hidden">
            <div className="p-4 border-b border-border">
              <h3 className="text-xl font-semibold text-text-title">
                Dispositivos Ativos Recentes
              </h3>
            </div>

            <div className="overflow-x-auto">
              {devicesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="size-8 animate-spin text-primary" />
                </div>
              ) : recentDevices.length > 0 ? (
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      {['Status', 'Usuário', 'Dispositivo', 'Versão App', 'Última Atividade'].map(
                        (col) => (
                          <th
                            key={col}
                            className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-title"
                          >
                            {col}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-card">
                    {recentDevices.map((device) => (
                      <tr key={device.id} className="transition-colors hover:bg-muted/50">
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span
                              className={`size-2.5 rounded-full ${
                                device.status === 'online' ? 'bg-online' : 'bg-offline'
                              }`}
                            />
                            <span className="text-sm capitalize text-text-secondary">
                              {device.status}
                            </span>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="text-sm font-medium text-text-title">
                            {device.userName}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="text-sm text-text-body">{device.model}</div>
                          <div className="text-xs capitalize text-text-secondary">
                            {device.platform}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 font-mono text-sm tabular-nums text-text-body">
                          {device.appVersion}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-text-secondary">
                          {formatRelativeTime(device.lastActivity)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="flex items-center justify-center py-12">
                  <p style={{ color: '#57636C' }}>Nenhum dispositivo ativo recente</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
